/**
 * Reference target-curve overlays for the Frequency Response chart —
 * Sonarworks/Dirac-style "compare against a shape" line. A registry, not a
 * fixed set: add or remove an entry here and it's immediately selectable,
 * no other file needs to change.
 *
 * Each curve is a SHAPE, not an absolute SPL target — the FR chart itself
 * displays relative dB (mean-shifted, see series.ts's shiftDbToMean), so a
 * target curve is normalised the same way (its own mean subtracted) before
 * being plotted, putting it on the same relative scale as the measured
 * trace. None of these claim to reproduce a specific published standard
 * (e.g. the Harman curve) exactly — they're honestly-labelled reference
 * shapes for "does my room's balance roughly follow this general contour",
 * not a certified target.
 */
import { shiftDbToMean } from './series';

export interface TargetCurveDefinition {
  id: string;
  label: string;
  description: string;
  /** Relative dB at a given frequency, BEFORE mean-normalisation. Only the
   *  shape matters — evalTargetCurve subtracts this function's own mean
   *  over the requested frequency set before returning it. */
  dbAt(freqHz: number): number;
}

export const TARGET_CURVE_REGISTRY: TargetCurveDefinition[] = [
  {
    id: 'flat',
    label: 'Flat',
    description: 'A flat reference — deviation from this line is deviation from flat.',
    dbAt: () => 0,
  },
  {
    id: 'gentle-tilt',
    label: 'Gentle tilt',
    description:
      'A smooth downward tilt (bass slightly up, treble slightly down, centred ~500 Hz) — the general ' +
      'shape many consumer speaker/room target curves aim for. A stylised reference shape, not a ' +
      'calibrated standard (e.g. not a reproduction of the published Harman curve).',
    dbAt: (f) => 3 - 6 / (1 + Math.exp(-Math.log2(f / 500))),
  },
];

/** Target curve dB values at each of `freqs`, mean-normalised onto the same
 *  relative scale the FR chart's measured/baseline traces use. Null if `id`
 *  isn't registered (e.g. 'none') or `freqs` is empty. */
export function evalTargetCurve(id: string, freqs: number[]): number[] | null {
  if (!freqs.length) return null;
  const def = TARGET_CURVE_REGISTRY.find((d) => d.id === id);
  if (!def) return null;
  const raw = freqs.map((f) => def.dbAt(f));
  // targetMean 0, not shiftDbToMean's default 80 (an SPL-reference convention
  // that doesn't apply here) — this curve is a relative SHAPE, meant to sit
  // on the same 0-centred relative scale the FR chart's own traces use.
  return shiftDbToMean(raw, raw, 0);
}
